// DESIGN-RULES R7 / SPEC-web-planner-033 AC-11 — "did the user enter anything since this popup opened".
// A popup calls reset(state) when it opens and request(state, close) from Cancel / ×: nothing changed closes at once,
// otherwise `asking` turns on the discard ask (<app-discard-confirm>). The state is compared as a snapshot, because
// FormGroup.dirty misses programmatic patches and custom controls (audience picker, repeaters, files).
export class DiscardGuard {
    /** True while the "Discard your changes?" ask is showing. */
    asking = false;
    private baseline = '';
    private onDiscard: (() => void) | null = null;

    /** Take the state the popup opened with. */
    reset(state: unknown): void {
        this.baseline = snapshotOf(state);
        this.asking = false;
        this.onDiscard = null;
    }

    isDirty(state: unknown): boolean {
        return snapshotOf(state) !== this.baseline;
    }

    /** Cancel / ×: close now when nothing was entered, otherwise ask first. */
    request(state: unknown, close: () => void): void {
        if (!this.isDirty(state)) {
            close();
            return;
        }
        this.onDiscard = close;
        this.asking = true;
    }

    /** "Keep editing": back to the form, untouched. */
    keep(): void {
        this.asking = false;
        this.onDiscard = null;
    }

    /** "Discard": close the popup. */
    discard(): void {
        const close = this.onDiscard;
        this.asking = false;
        this.onDiscard = null;
        close?.();
    }
}

/** Order-independent, File-aware JSON of a state, for comparing "now" with "at open". */
export function snapshotOf(state: unknown): string {
    return JSON.stringify(canonical(state));
}

function canonical(v: any): any {
    if (typeof File !== 'undefined' && v instanceof File) {
        return `file:${v.name}:${v.size}:${v.lastModified}`;
    }
    if (v instanceof Date) { return v.toISOString(); }
    if (Array.isArray(v)) { return v.map(canonical); }
    if (v && typeof v === 'object') {
        const out: Record<string, any> = {};
        for (const k of Object.keys(v).sort()) {
            if (v[k] !== undefined) { out[k] = canonical(v[k]); }
        }
        return out;
    }
    return v;
}
